import {useEffect, useRef, useState} from 'react'

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled])'

/**
 * The popover surface the Language picker, the Voice picker and the account menu share: opening
 * moves focus inside (onto the current choice when there is one), Escape and an outside click
 * close, Escape returns focus to the trigger, and a trigger disabled while open closes it.
 */
export function usePopover(disabled = false) {
    const [open, setOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const popoverRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (disabled) setOpen(false)
    }, [disabled])

    useEffect(() => {
        if (!open) return
        const panel = popoverRef.current
        const current = panel?.querySelector<HTMLElement>('[aria-current="true"], input:checked')
        ;(current ?? panel?.querySelector<HTMLElement>(FOCUSABLE))?.focus()

        const onClick = (e: MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            setOpen(false)
            triggerRef.current?.focus()
        }
        document.addEventListener('mousedown', onClick)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onClick)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    return {open, setOpen, containerRef, triggerRef, popoverRef}
}
